'use client';

import { useState, useEffect, useMemo } from 'react';
import { messagesApi } from '@/lib/api/messages';
import type {
  Conversation,
  Message,
  MessageSender,
  CreateConversationInput,
  CreateMessageInput,
} from '@/lib/types/message.types';
import type { QueryParams } from '@/lib/types/common.types';
import { useAuth } from '@/contexts/AuthContext';
import { useNotificationWebSocket } from '@/contexts/NotificationWebSocketContext';
import type { MessageEvent, NotificationMessage } from '@/lib/websocket/notificationClient';

export function useConversations(params?: QueryParams) {
  const [data, setData] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    // Only fetch if user is authenticated
    if (!isAuthenticated) {
      setData([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    const fetchConversations = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await messagesApi.getAllConversations(params);
        setData(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    fetchConversations();
    // params should be memoized by the caller to prevent unnecessary re-renders
  }, [params, isAuthenticated]);

  return { data, isLoading, error };
}

export function useConversation(id: string) {
  const [data, setData] = useState<Conversation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    const fetchConversation = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await messagesApi.getConversation(id);
        setData(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    fetchConversation();
  }, [id]);

  return { data, isLoading, error };
}

export function useMessages(conversationId: string, params?: QueryParams) {
  const [data, setData] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { onMessage } = useNotificationWebSocket();
  const memoizedConversationId = useMemo(() => conversationId, [conversationId]);

  useEffect(() => {
    if (!memoizedConversationId) {
      setIsLoading(false);
      return;
    }

    const fetchMessages = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await messagesApi.getMessages(memoizedConversationId, params);
        setData(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessages();
  }, [memoizedConversationId, params]);

  // Listen for real-time message events and apply if they belong to this conversation
  useEffect(() => {
    if (!memoizedConversationId) return;

    const unsubscribe = onMessage((event: MessageEvent) => {
      if (event.conversation_id !== memoizedConversationId) return;

      if (event.type === 'new_message') {
        const payload = event.message;
        setData((prev) => {
          // Avoid duplicates
          if (prev.some((item) => item.id === payload.id)) {
            return prev;
          }

          const createdAtMs = (payload.created_at ?? Date.now()) * 1000;
          const updatedAtMs = (payload.updated_at ?? payload.created_at ?? Date.now()) * 1000;

          const mapped: Message = {
            id: payload.id,
            conversationId: payload.conversation_id,
            senderId: payload.sender_id,
            sender: payload.sender
              ? {
                  id: payload.sender.id,
                  fullName: payload.sender.full_name,
                  avatar: payload.sender.avatar,
                  userType: payload.sender.user_type as MessageSender['userType'],
                }
              : undefined,
            content: payload.content,
            messageType: (payload.message_type as Message['messageType']) || 'text',
            attachments: Array.isArray(payload.attachments)
              ? (payload.attachments as Message['attachments'])
              : [],
            isRead: payload.is_read ?? false,
            readAt: null,
            isSystemMessage: payload.is_system_message ?? false,
            createdAt: new Date(createdAtMs).toISOString(),
            updatedAt: new Date(updatedAtMs).toISOString(),
          };

          // Remove any temp messages with matching content and sender (optimistic updates cleanup)
          const filtered = prev.filter(
            (msg) =>
              !msg.id.startsWith('temp-') ||
              !(msg.content === mapped.content && msg.senderId === mapped.senderId)
          );

          return [...filtered, mapped];
        });
        return;
      }

      if (event.type === 'message_updated') {
        const updatedAtMs = (event.message.updated_at ?? Date.now()) * 1000;
        setData((prev) =>
          prev.map((msg) =>
            msg.id === event.message.id
              ? {
                  ...msg,
                  content: event.message.content,
                  updatedAt: new Date(updatedAtMs).toISOString(),
                }
              : msg
          )
        );
        return;
      }

      if (event.type === 'message_deleted') {
        setData((prev) => prev.filter((msg) => msg.id !== event.message_id));
        return;
      }

      if (event.type === 'message_read') {
        const readAtMs = (event.read_at ?? Date.now()) * 1000;
        setData((prev) =>
          prev.map((msg) =>
            msg.id === event.message_id
              ? { ...msg, isRead: true, readAt: new Date(readAtMs).toISOString() }
              : msg
          )
        );
        return;
      }

      if (event.type === 'messages_all_read') {
        const readAtMs = (event.read_at ?? Date.now()) * 1000;
        setData((prev) =>
          prev.map((msg) => {
            const createdAtMs = new Date(msg.createdAt).getTime();
            const shouldMarkRead =
              msg.senderId !== event.read_by && createdAtMs <= readAtMs;
            if (!shouldMarkRead) return msg;
            return { ...msg, isRead: true, readAt: new Date(readAtMs).toISOString() };
          })
        );
      }
    });

    return unsubscribe;
  }, [memoizedConversationId, onMessage]);

  const patchMessage = (messageId: string, patch: Partial<Message>) => {
    setData((prev) => prev.map((msg) => (msg.id === messageId ? { ...msg, ...patch } : msg)));
  };

  const markAllAsReadLocally = (readBy: string, readAtIso?: string) => {
    const readAtMs = readAtIso ? new Date(readAtIso).getTime() : Date.now();
    setData((prev) =>
      prev.map((msg) => {
        const createdAtMs = new Date(msg.createdAt).getTime();
        const shouldMarkRead = msg.senderId !== readBy && createdAtMs <= readAtMs;
        if (!shouldMarkRead) return msg;
        return { ...msg, isRead: true, readAt: new Date(readAtMs).toISOString() };
      })
    );
  };

  // Function to optimistically add a message
  const addOptimisticMessage = (message: Message) => {
    setData(prev => {
      // Avoid duplicates
      if (prev.some(item => item.id === message.id)) {
        return prev;
      }
      return [...prev, message];
    });
  };

  // Function to remove a message (useful for cleaning up temp messages)
  const removeMessage = (messageId: string) => {
    setData(prev => prev.filter(msg => msg.id !== messageId));
  };

  return {
    data,
    isLoading,
    error,
    addOptimisticMessage,
    removeMessage,
    patchMessage,
    markAllAsReadLocally,
  };
}

export function useUnreadCount() {
  const [data, setData] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    // Only fetch if user is authenticated
    if (!isAuthenticated) {
      setData(0);
      setIsLoading(false);
      setError(null);
      return;
    }

    const fetchUnreadCount = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await messagesApi.getUnreadCount();
        setData(result.count);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    fetchUnreadCount();
  }, [isAuthenticated]);

  return { data, isLoading, error };
}

export function useCreateConversation() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = async (data: CreateConversationInput) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await messagesApi.createConversation(data);
      setIsLoading(false);
      return result;
    } catch (err) {
      setError(err as Error);
      setIsLoading(false);
      throw err;
    }
  };

  return { mutate, isLoading, error };
}

export function useSendSystemMessage() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = async (conversationId: string, content: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await messagesApi.sendSystemMessage(conversationId, content);
      setIsLoading(false);
      return result;
    } catch (err) {
      setError(err as Error);
      setIsLoading(false);
      throw err;
    }
  };

  return { mutate, isLoading, error };
}

export function useSendMessage() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = async (
    conversationId: string,
    messageData: CreateMessageInput,
    attachments?: File[]
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await messagesApi.sendMessage(conversationId, messageData, attachments);
      setIsLoading(false);
      return result;
    } catch (err) {
      setError(err as Error);
      setIsLoading(false);
      throw err;
    }
  };

  return { mutate, isLoading, error };
}

export function useUpdateMessage() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = async (id: string, content: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await messagesApi.updateMessage(id, content);
      setIsLoading(false);
      return result;
    } catch (err) {
      setError(err as Error);
      setIsLoading(false);
      throw err;
    }
  };

  return { mutate, isLoading, error };
}

export function useDeleteMessage() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await messagesApi.deleteMessage(id);
      setIsLoading(false);
    } catch (err) {
      setError(err as Error);
      setIsLoading(false);
      throw err;
    }
  };

  return { mutate, isLoading, error };
}
